import { DEFAULT_CONFIG } from './config.js';
import { clamp } from './util.js';
import { playableRecommendation, validContractPrice } from './decision.js';
/**
 * Ports `calculateAllocation()`: quarter-Kelly sizing from a $200 pool
 * capped at a $20 buy. Binary $1-payout Kelly: full-Kelly fraction is
 * `(p - price) / (1 - price)`; quarter Kelly is applied because probability
 * estimates are noisy, not because of any fee model (fees are handled by a
 * later, execution-layer gate — see plan section 7).
 */
export function calculateAllocation(recommendation, probability, contractPrice, bankroll = DEFAULT_CONFIG.simulatedStartingPool, config = DEFAULT_CONFIG) {
    if (!playableRecommendation(recommendation)) {
        return {
            stake: 0,
            edge: null,
            fullKellyFraction: 0,
            appliedFraction: 0,
            reason: 'Recommendation is SKIP',
        };
    }
    const rawProbability = Number(probability);
    const price = Number(contractPrice);
    const available = Math.max(0, Number(bankroll) || 0);
    if (!Number.isFinite(rawProbability)) {
        return {
            stake: 0,
            edge: null,
            fullKellyFraction: 0,
            appliedFraction: 0,
            reason: 'No saved confidence score',
        };
    }
    const p = clamp(rawProbability, 0.5, 0.92);
    if (!validContractPrice(price)) {
        return {
            stake: 0,
            edge: null,
            fullKellyFraction: 0,
            appliedFraction: 0,
            reason: 'No actionable contract price',
        };
    }
    const edge = p - price;
    if (edge < config.sizingMinEdge) {
        return {
            stake: 0,
            edge,
            fullKellyFraction: 0,
            appliedFraction: 0,
            reason: `Edge below ${(config.sizingMinEdge * 100).toFixed(0)}% minimum`,
        };
    }
    const fullKellyFraction = clamp(edge / (1 - price), 0, 1);
    const appliedFraction = fullKellyFraction * config.kellyFraction;
    const rawStake = available * appliedFraction;
    let stake = Math.min(rawStake, config.maxIndividualBuy, available);
    if (stake < config.minimumIndividualBuy)
        stake = 0;
    stake = Math.round(stake * 100) / 100;
    return {
        stake,
        edge,
        fullKellyFraction,
        appliedFraction,
        reason: stake > 0
            ? rawStake > config.maxIndividualBuy
                ? 'Capped at max buy'
                : 'Quarter-Kelly sizing'
            : 'Calculated buy is below minimum',
    };
}
//# sourceMappingURL=sizing.js.map